import { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { cloneDeep } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";
import { baseUrl } from "@/consts";
import { InventoryCountFull, Supplier } from "@/interfaces";
import { useIsAdminMode } from "@/contexts";
import { useDataApi } from "@/hooks";
import {
  formatAxiosError,
  formatDateToPST,
  formatName,
  isNonEmptyArray,
  isNonEmptyString,
  isNumber,
  isStringEqualCI,
  tryParseInt,
} from "@/utils";
import {
  colors,
  sxButton,
  sxFont_12_500,
  sxFont_14_400,
  sxFont_14_500,
  sxFont_18_500,
} from "@/theme";
import { ErrorMessage } from "@/components/atoms";
import { SupplierHelper } from "@/helpers";

export type InventoryCountEditDialogProps = {
  inventoryCount?: InventoryCountFull;
  suppliers?: Supplier[];
  open?: boolean;
  onClose?: (inventoryCount?: InventoryCountFull) => void;
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('"Inventory Count Name" is required'),
  suppliers: Yup.array().min(1, '"Supplier" is required'),
});

const initialValues = {
  inventoryCountId: undefined as unknown as number,
  name: undefined,
  suppliers: [] as Supplier[],
};

const getCreatorInfo = (label?: string, value?: string) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr 3fr",
      color: colors.neutralGrey800,
      padding: "8px 0",
    }}
  >
    <Typography component="div" sx={{ ...sxFont_14_500 }}>
      {label}
    </Typography>
    <Typography component="div" sx={{ ...sxFont_14_400 }}>
      {value}
    </Typography>
  </Box>
);

const InventoryCountEditDialog: React.FC<InventoryCountEditDialogProps> = ({
  inventoryCount: _inventoryCount,
  suppliers,
  open = false,
  onClose,
}) => {
  const { currentAdminData } = useIsAdminMode();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const { post, put, error } = useDataApi(baseUrl.INVENTORY_COUNTS);

  const { errors, touched, handleBlur, ...formik } = useFormik({
    enableReinitialize: true,
    validationSchema: validationSchema,
    initialValues: cloneDeep(_inventoryCount || initialValues),
    onSubmit(values, { validateForm }) {
      validateForm(values).then(() => onConfirmButtonClick(values));
    },
  });

  useEffect(() => {
    if (error?.config?.url?.indexOf(baseUrl.INVENTORY_COUNTS) >= 0) {
      setErrorMessage(formatAxiosError(error));
    }
  }, [error]);

  const onConfirmButtonClick = async (inventoryCount?: InventoryCountFull) => {
    setErrorMessage(undefined);

    if (!inventoryCount || !formik.dirty) {
      onClose?.();
      return;
    }

    let _error = undefined;

    try {
      validationSchema.validateSync(inventoryCount);

      if (!isNumber(inventoryCount.inventoryCountId)) {
        const now = new Date();
        await post?.(baseUrl.INVENTORY_COUNTS, {
          ...inventoryCount,
          dateStarted: now,
          createdBy: currentAdminData?.email,
          createdOn: now,
        });
      } else {
        await put?.(
          `${baseUrl.INVENTORY_COUNTS}/${inventoryCount.inventoryCountId}`,
          inventoryCount
        );
      }
    } catch (e) {
      _error = true;

      if (e instanceof Yup.ValidationError) {
        if (isNonEmptyArray(e.errors)) {
          setErrorMessage(e.errors[0]);
        } else {
          setErrorMessage("Unknown validation error");
          console.log(e);
        }
      } else {
        console.log(e);
      }
    }

    if (error || _error) {
      return;
    }

    formik.resetForm();
    onClose?.(inventoryCount);
  };

  const handleClose = (_: any, reason: string) => {
    if (isStringEqualCI(reason, "backdropClick")) {
      return;
    }

    onConfirmButtonClick();
  };

  const handleCheckboxSupplierChanged = (
    e: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const supplierId = tryParseInt(e.target.value);
    const tmp = cloneDeep(formik.values.suppliers || []);
    const idx = SupplierHelper.findSupplierIndex(
      formik.values.suppliers,
      supplierId
    );

    let isChanged = false;

    if (checked) {
      if (idx === -1) {
        tmp?.push?.({
          supplierId,
          supplierName: (
            SupplierHelper.findSupplier(suppliers, supplierId) as Supplier
          )?.supplierName,
        });
        isChanged = true;
      }
    } else {
      if (idx !== -1) {
        tmp?.splice?.(idx, 1);
        isChanged = true;
      }
    }

    if (isChanged) {
      formik.setFieldValue("suppliers", tmp);
    }
  };

  return (
    <Dialog open={open} maxWidth="md" onClose={handleClose}>
      <DialogTitle sx={{ borderBottom: "1px solid " + colors.neutralGrey400 }}>
        <Typography sx={{ ...sxFont_18_500, textAlign: "center" }}>
          {`${_inventoryCount ? "Edit" : "Create"} Count`}
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => handleClose(undefined, undefined as unknown as string)}
        sx={{
          position: "absolute",
          right: 20,
          top: 10,
          height: "30px",
          width: "30px",
          color: colors.white,
          backgroundColor: colors.primaryOzone500,
          "&:hover": {
            color: colors.white,
            background: colors.primaryOzone500,
          },
        }}
      >
        <Close fontSize="small" />
      </IconButton>
      <DialogContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            rowGap: "16px",
            padding: "10px",
          }}
        >
          <TextField
            id="name"
            name="name"
            label="Inventory Count Name"
            required
            value={formik.values.name}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("name", value)
            }
            onBlur={handleBlur}
            error={isNonEmptyString(errors.name) && touched.name}
            helperText={errors.name}
            fullWidth
            variant="outlined"
          />
          {isNonEmptyArray(suppliers) ? (
            <Box>
              <Typography
                component="div"
                sx={{ ...sxFont_12_500, color: colors.neutralGrey800 }}
              >
                SUPPLIER
              </Typography>
              <Box>
                {suppliers?.map((item) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{
                          color: colors.neutralGrey800, '&.Mui-checked': {
                            color: colors.neutralGrey800,
                          },
                        }}
                        value={item.supplierId}
                        checked={SupplierHelper.supplierExists(
                          formik.values.suppliers,
                          item.supplierId
                        )}
                        onChange={handleCheckboxSupplierChanged}
                      />
                    }
                    label={item.supplierName}
                    key={item.supplierId}
                    sx={{ ...sxFont_14_400 }}
                  />
                ))}
              </Box>
            </Box>
          ) : undefined}
          <Box>
            {getCreatorInfo(
              "Created by",
              _inventoryCount
                ? formatName(
                  _inventoryCount?.createdBy?.split("@").at(0),
                  _inventoryCount?.dealerLastName
                )
                : currentAdminData?.email
            )}
            {getCreatorInfo(
              "Created on",
              formatDateToPST(
                (_inventoryCount?.createdOn || new Date()) as Date
              )
            )}
          </Box>
        </Box>
        {!!errorMessage ? <ErrorMessage errorMessage={errorMessage} /> : null}
      </DialogContent>
      <DialogActions sx={{ bgcolor: colors.primaryOzone500 }}>
        <Button
          onClick={() =>
            onConfirmButtonClick(formik.values as InventoryCountFull)
          }
          autoFocus
          sx={{ ...sxButton, width: "100%", bgcolor: "transparent" }}
        >
          {`${_inventoryCount ? "Save" : "Create"} Count`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryCountEditDialog;
